import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { variantConfig } from '../config/appVariant';
import MinistryEmblem from './MinistryEmblem';
import Icon from './Icon';
import useGlassIndicator from './useGlassIndicator';
import { isAllowedImageSrc } from '../utils/safeMarkdown';
import styles from './Sidebar.module.css';

const Sidebar = ({ isOpen, onClose, onStartTour }) => {
  const { t, language, toggleLanguage, isRTL } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    theme,
    toggleTheme,
    currentUser,
    logout,
    chatHistory,
    currentChat,
    createNewChat,
    deleteChat,
    loadChat,
    knowledgeBaseFiles,
    // Document history
    documentHistory,
    currentDocument,
    loadDocumentHistory,
    loadDocument,
    createNewDocument,
    deleteDocument,
    // Correction history
    correctionHistory,
    currentCorrection,
    loadCorrectionHistory,
    deleteCorrectionFromHistory,
    showNotification,
    loadCorrectionSession,
    createNewCorrection,
  } = useApp();

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const accountRef = useRef(null);
  const navRef = useRef(null);

  // Menus dismiss on Escape or a click anywhere else.
  useEffect(() => {
    if (!showUserMenu) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowUserMenu(false); };
    const onDown = (e) => { if (!accountRef.current?.contains(e.target)) setShowUserMenu(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [showUserMenu]);

  const DOC_PATH = variantConfig.documentPath;
  // First letters of the first two words; the joiner keeps Arabic letters apart.
  const initials = (currentUser?.username || '').trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map((word) => word[0].toUpperCase()).join('\u200C') || '?';
  // Pages only; history rows use aria-current="true" and a plain fill.
  useGlassIndicator(navRef, [location.pathname, isCollapsed, language], '[aria-current="page"]');
  const isOnMainChat = location.pathname === '/';
  const isOnHomeworkGenerator = location.pathname === DOC_PATH || location.pathname === '/homework-generator';
  const isOnCorrector = location.pathname === '/homework-corrector';

  // Load document history when navigating to homework generator
  useEffect(() => {
    if (isOnHomeworkGenerator && currentUser && !currentUser.isGuest) {
      loadDocumentHistory(currentUser.id);
    }
  }, [isOnHomeworkGenerator, currentUser?.id]);

  // Load correction history when navigating to homework corrector
  useEffect(() => {
    if (isOnCorrector && currentUser && !currentUser.isGuest) {
      loadCorrectionHistory(currentUser.id);
    }
  }, [isOnCorrector, currentUser?.id]);

  const workspaceItems = [
    { id: 'home', icon: 'home', label: t('sidebar.home'), path: '/', description: t('sidebar.home') },
    { id: 'knowledge', icon: 'database', label: t('sidebar.knowledgeBases'), path: '/knowledge-bases', description: t('sidebar.knowledgeBases'), badge: knowledgeBaseFiles.length || null }
  ];

  // Helpers (the "HELPERS"/"TOOLS" nav section) come from the active product
  // variant so the general employee build never shows teaching-only tools.
  // The teacher variant carries the consolidated (reorganized) layout defined
  // in appVariant.js; general keeps its own reduced tool set.
  const helpers = variantConfig.helpers.map(h => ({
    id: h.id,
    path: h.path,
    matchPaths: h.matchPaths || [h.path],
    icon: h.icon,
    name: isRTL ? h.nameAr : h.nameEn,
    desc: isRTL ? h.descAr : h.descEn,
  }));

  // ===== Chat actions =====
  const handleNewChat = () => {
    createNewChat();
    if (window.innerWidth <= 768) onClose();
  };

  const handleChatClick = (sessionId) => {
    loadChat(sessionId);
    navigate('/');
    if (window.innerWidth <= 768) onClose();
  };

  // Confirm, wait for the server, then say how it went. `deleteFn` resolves to
  // whether the server confirmed; the item leaves the list only if it did.
  const confirmAndDelete = async (e, sessionId, deleteFn, msg) => {
    e.stopPropagation();
    if (!window.confirm(msg.confirm)) return;
    const deleted = await deleteFn(sessionId);
    showNotification(deleted ? msg.done : msg.failed, deleted ? 'success' : 'error');
  };

  const handleDeleteChat = (e, sessionId) => confirmAndDelete(e, sessionId, deleteChat, { confirm: t('sidebar.deleteChatConfirm'), done: t('sidebar.chatDeleted'), failed: t('sidebar.deleteChatFailed') });

  // ===== Correction actions =====
  const handleNewCorrection = () => {
    createNewCorrection();
    navigate('/homework-corrector');
    if (window.innerWidth <= 768) onClose();
  };

  const handleCorrectionClick = (sessionId) => {
    loadCorrectionSession(sessionId);
    navigate('/homework-corrector');
    if (window.innerWidth <= 768) onClose();
  };

  const handleDeleteCorrection = (e, sessionId) => confirmAndDelete(e, sessionId, deleteCorrectionFromHistory, { confirm: t('sidebar.deleteAssessmentConfirm'), done: t('sidebar.assessmentDeleted'), failed: t('sidebar.deleteAssessmentFailed') });

  // ===== Document actions =====
  const handleNewDocument = () => {
    createNewDocument();
    navigate(DOC_PATH);
    if (window.innerWidth <= 768) onClose();
  };

  const handleDocumentClick = (sessionId) => {
    loadDocument(sessionId);
    navigate(DOC_PATH);
    if (window.innerWidth <= 768) onClose();
  };

  const handleDeleteDocument = (e, sessionId) => confirmAndDelete(e, sessionId, deleteDocument, { confirm: t('sidebar.deleteDocumentConfirm'), done: t('sidebar.documentDeleted'), failed: t('sidebar.deleteDocumentFailed') });

  // History rows hold a nested delete button, so they can't be <button>s;
  // this gives them the same keyboard behaviour (Enter / Space).
  const rowProps = (onActivate) => ({
    role: 'button',
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (e) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); }
    },
  });

  const handleNavigation = () => {
    if (window.innerWidth <= 768) onClose();
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    // The server sent no time for this item: show nothing rather than a wrong one.
    if (!timestamp || isNaN(date)) return '';
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (isRTL) {
      if (diffMins < 1) return 'الآن';
      if (diffMins < 60) return `منذ ${diffMins} د`;
      if (diffHours < 24) return `منذ ${diffHours} س`;
      if (diffDays < 7) return `منذ ${diffDays} ي`;
      return date.toLocaleDateString('ar-SA');
    } else {
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString('en-US');
    }
  };

  // Chat history is grouped by day, so each row only shows the part of the
  // date its heading doesn't already say. Chats the server sent no time for
  // sort last, under their own heading.
  const chatDateParts = (timestamp) => {
    const date = new Date(timestamp);
    if (!timestamp || isNaN(date)) return { group: t('sidebar.earlier'), time: '' };
    const locale = isRTL ? 'ar-SA' : 'en-US';
    const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const daysAgo = Math.round((dayStart(new Date()) - dayStart(date)) / 86400000);
    if (daysAgo <= 1) {
      return {
        group: t(daysAgo === 1 ? 'sidebar.yesterday' : 'sidebar.today'),
        time: date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' }),
      };
    }
    if (daysAgo <= 7) {
      return { group: t('sidebar.previous7Days'), time: date.toLocaleDateString(locale, { weekday: 'short' }) };
    }
    return {
      group: date.toLocaleDateString(locale, { month: 'long', year: 'numeric' }),
      time: date.toLocaleDateString(locale, { month: 'short', day: 'numeric' }),
    };
  };
  const chatRows = [...chatHistory]
    .sort((a, b) => (Date.parse(b.timestamp) || 0) - (Date.parse(a.timestamp) || 0))
    .map((chat) => ({ chat, ...chatDateParts(chat.timestamp) }));

  // Decide what the primary action button does
  const showNewChatBtn = isOnMainChat;
  const showNewDocBtn = isOnHomeworkGenerator;
  const showNewCorrectionBtn = isOnCorrector;

  return (
    <div className={`${styles.sidebar} ${isOpen ? styles.open : ''} ${isCollapsed ? styles.collapsed : ''}`}>
      <div className={styles.sidebarHeader}>
        {!isCollapsed && (
          <div className={styles.brand}>
            <MinistryEmblem height={34} alt={t('sidebar.ministry')} />
            <span className={styles.brandDivider} aria-hidden="true" />
            <span className={styles.brandName}>{t('common.appName')}</span>
            <span className={styles.betaBadge} title={t('common.betaHint')}>{t('common.beta')}</span>
          </div>
        )}
        <button className={styles.collapseBtn} onClick={() => {
          if (window.innerWidth <= 768) {
            onClose();
          } else {
            setIsCollapsed(prev => !prev);
          }
        }} title={isCollapsed ? t('sidebar.showSidebar') : t('sidebar.hideSidebar')}
          aria-label={isCollapsed ? t('sidebar.showSidebar') : t('sidebar.hideSidebar')}
          aria-expanded={!isCollapsed}>
          <Icon name="left_panel_close" className={styles.iconDesktop} />
          <Icon name="close" className={styles.iconMobile} />
        </button>
      </div>

      {/* Collapsed icon actions */}
      <div className={styles.collapsedActions}>
        {(showNewChatBtn || showNewDocBtn || showNewCorrectionBtn) && (
          <button
            className={`${styles.collapsedActionBtn} ${styles.newChat}`}
            onClick={showNewDocBtn ? handleNewDocument : showNewCorrectionBtn ? handleNewCorrection : handleNewChat}
            title={showNewDocBtn ? t('sidebar.newDocument') : showNewCorrectionBtn ? t('sidebar.newAssessment') : t('sidebar.newChat')}
            aria-label={showNewDocBtn ? t('sidebar.newDocument') : showNewCorrectionBtn ? t('sidebar.newAssessment') : t('sidebar.newChat')}
          >
            <Icon name="add" />
          </button>
        )}
      </div>

      {/* Primary action button — adapts per page */}
      {!isCollapsed && showNewChatBtn && (
        <button className={styles.newChatBtn} onClick={handleNewChat}>
          <Icon name="add" />
          {t('sidebar.newChat')}
        </button>
      )}
      {!isCollapsed && showNewDocBtn && (
        <button className={styles.newChatBtn} onClick={handleNewDocument}>
          <Icon name="add" />
          {t('sidebar.newDocument')}
        </button>
      )}
      {!isCollapsed && showNewCorrectionBtn && (
        <button className={styles.newChatBtn} onClick={handleNewCorrection}>
          <Icon name="add" />
          {t('sidebar.newAssessment')}
        </button>
      )}

      <nav ref={navRef} data-tour="nav" className={`${styles.sidebarContent} glass-track glass-track--nav`} aria-label={t('sidebar.navigation')}>
        {/* Workspace */}
        <div className={styles.workspaceSection}>
          <div className={styles.sectionTitle}>{t('sidebar.workspace')}</div>
          {workspaceItems.map(item => (
            <Link key={item.id} to={item.path} data-tour={`nav-${item.id}`} className={`${styles.navItem} ${location.pathname === item.path ? styles.active : ''}`} onClick={handleNavigation} title={item.description} aria-current={location.pathname === item.path ? 'page' : undefined}>
              <Icon name={item.icon} />
              <span>{item.label}</span>
              {item.badge && <span className={styles.navBadge}>{item.badge}</span>}
            </Link>
          ))}
        </div>

        {/* Helpers */}
        <div className={styles.helpersSection}>
          <div className={styles.sectionTitle}>{t('sidebar.helpers')}</div>
          {helpers.map(helper => {
            const matchPaths = helper.matchPaths || [helper.path];
            const isActive = matchPaths.some((path) => {
              return location.pathname === path || location.pathname.startsWith(`${path}/`);
              });
            return (
            <Link key={helper.id} to={helper.path} data-tour={`nav-${helper.id}`} className={`${styles.helperItem} ${isActive ? styles.active : ''}`} onClick={handleNavigation} aria-current={isActive ? 'page' : undefined} title={helper.desc}>
              <div className={styles.helperIcon}><Icon name={helper.icon} /></div>
              <div className={styles.helperInfo}>
                <div className={styles.helperName}>
                  {helper.name}
                  {helper.badge && <span className={styles.helperBadge}>{helper.badge}</span>}
                </div>
                <div className={styles.helperDesc}>{helper.desc}</div>
              </div>
            </Link>
            );
          })}
        </div>

        {/* Support & feedback — its own section, below the tools it is about.
            Reuses the helper item styling so it reads as a peer of them. */}
        <div className={styles.feedbackSection}>
          <Link
            to="/feedback"
            data-tour="nav-feedback"
            className={`${styles.helperItem} ${location.pathname === '/feedback' ? styles.active : ''}`}
            onClick={handleNavigation}
            aria-current={location.pathname === '/feedback' ? 'page' : undefined}
            title={t('sidebar.feedbackDesc')}
          >
            <div className={styles.helperIcon}><Icon name="forum" /></div>
            <div className={styles.helperInfo}>
              <div className={styles.helperName}>{t('sidebar.feedback')}</div>
              <div className={styles.helperDesc}>{t('sidebar.feedbackDesc')}</div>
            </div>
          </Link>
        </div>

        {/* ===== Chat History (only on main chat page) ===== */}
        {isOnMainChat && (
          <div className={styles.chatHistorySection}>
            <div className={styles.sectionTitle}>{t('sidebar.chatHistory')}</div>
            <div className={styles.chatHistoryList}>
              {chatHistory.length === 0 ? (
                <div className={styles.emptyHistory}>
                  <Icon name="forum" />
                  <p>{t('sidebar.noChatsYet')}</p>
                </div>
              ) : (
                chatRows.map(({ chat, group, time }, i) => (
                  <React.Fragment key={chat.sessionId}>
                    {group !== chatRows[i - 1]?.group && <div className={styles.chatGroupLabel}>{group}</div>}
                    <div className={`${styles.chatItem} ${chat.sessionId === currentChat?.sessionId ? styles.active : ''}`} {...rowProps(() => handleChatClick(chat.sessionId))}>
                      <Icon name="chat_bubble" className={styles.chatItemIcon} />
                      <div className={styles.chatItemInfo}>
                        <div className={styles.chatItemTitle}>{chat.title}</div>
                      </div>
                      <span className={styles.chatItemClock}>{time}</span>
                      <button className={styles.deleteChatBtn} onClick={(e) => handleDeleteChat(e, chat.sessionId)} title={t('sidebar.deleteChat')} aria-label={t('sidebar.deleteChat')}>
                        <Icon name="delete" />
                      </button>
                    </div>
                  </React.Fragment>
                ))
              )}
            </div>
          </div>
        )}

        {/* ===== Correction History (only on homework-corrector page) ===== */}
        {isOnCorrector && (
          <div className={styles.chatHistorySection}>
            <div className={styles.sectionTitle}>
              {t('sidebar.assessmentHistory')}
            </div>
            <div className={styles.chatHistoryList}>
              {correctionHistory.length === 0 ? (
                <div className={styles.emptyHistory}>
                  <Icon name="task_alt" />
                  <p>{t('sidebar.noAssessmentsYet')}</p>
                </div>
              ) : (
                correctionHistory.map(correction => (
                  <div
                    key={correction.sessionId}
                    className={`${styles.chatItem} ${correction.sessionId === currentCorrection?.sessionId ? styles.active : ''}`}
                    {...rowProps(() => handleCorrectionClick(correction.sessionId))}
                  >
                    <Icon name="task_alt" className={styles.chatItemIcon} />
                    <div className={styles.chatItemInfo}>
                      <div className={styles.chatItemTitle}>{correction.title}</div>
                      <div className={styles.chatItemTime}>
                        {correction.stats && (
                          <span className={styles.docSubjectTag}>{correction.stats.totalFiles} {t('sidebar.files')}{correction.stats.average != null && ` · ${correction.stats.average}%`}</span>
                        )}
                        {formatTimestamp(correction.timestamp)}
                      </div>
                    </div>
                    <button
                      className={styles.deleteChatBtn}
                      onClick={(e) => handleDeleteCorrection(e, correction.sessionId)}
                      title={t('sidebar.deleteAssessment')}
                      aria-label={t('sidebar.deleteAssessment')}
                    >
                      <Icon name="delete" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ===== Document History (only on homework-generator page) ===== */}
        {isOnHomeworkGenerator && (
          <div className={styles.chatHistorySection}>
            <div className={styles.sectionTitle}>
              {t('sidebar.documentHistory')}
            </div>
            <div className={styles.chatHistoryList}>
              {documentHistory.length === 0 ? (
                <div className={styles.emptyHistory}>
                  <Icon name="description" />
                  <p>{t('sidebar.noDocumentsYet')}</p>
                </div>
              ) : (
                documentHistory.map(doc => (
                  <div
                    key={doc.sessionId}
                    className={`${styles.chatItem} ${doc.sessionId === currentDocument?.sessionId ? styles.active : ''}`}
                    {...rowProps(() => handleDocumentClick(doc.sessionId))}
                  >
                    <Icon name="description" className={styles.chatItemIcon} />
                    <div className={styles.chatItemInfo}>
                      <div className={styles.chatItemTitle}>{doc.title || (t('sidebar.untitled'))}</div>
                      <div className={styles.chatItemTime}>
                        {doc.subject && <span className={styles.docSubjectTag}>{doc.subject}</span>}
                        {formatTimestamp(doc.timestamp)}
                      </div>
                    </div>
                    <button
                      className={styles.deleteChatBtn}
                      onClick={(e) => handleDeleteDocument(e, doc.sessionId)}
                      title={t('sidebar.deleteDocument')}
                      aria-label={t('sidebar.deleteDocument')}
                    >
                      <Icon name="delete" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </nav>

      {/* User section */}
      <div ref={accountRef}>
      <button type="button" data-tour="account" className={styles.userSection} onClick={() => setShowUserMenu(!showUserMenu)} aria-haspopup="menu" aria-expanded={showUserMenu}>
        {/* Initials are drawn here. A picture is used only from our own addresses:
            sessions cached before this change still hold a ui-avatars.com URL, and
            any outside avatar service would be sent the teacher's name. */}
        {isAllowedImageSrc(currentUser?.avatar)
          ? <img src={currentUser.avatar} alt="" className={styles.userAvatar} />
          : <span className={styles.userAvatar} aria-hidden="true">{initials}</span>}
        <div className={styles.userInfo}>
          <div className={styles.userName}>
            {currentUser?.username}
            {currentUser?.isGuest && <span className={styles.guestBadge}>{t('sidebar.guest')}</span>}
          </div>
          <div className={styles.userEmail}>{currentUser?.email}</div>
        </div>
        <Icon name={showUserMenu ? 'expand_more' : 'expand_less'} className={styles.userMenuIcon} />
      </button>

      <div className={`${styles.userMenu} ${showUserMenu ? styles.show : ''}`} role="menu">
        <button role="menuitem" className={styles.userMenuItem} onClick={() => { toggleLanguage(); setShowUserMenu(false); }} lang={language === 'en' ? 'ar' : 'en'}>
          <Icon name="language" />{language === 'en' ? 'العربية' : 'English'}
        </button>
        <button role="menuitem" className={styles.userMenuItem} onClick={() => { toggleTheme(); setShowUserMenu(false); }}>
          <Icon name={theme === 'light' ? 'dark_mode' : 'light_mode'} />
          {theme === 'light' ? t('sidebar.darkAppearance') : t('sidebar.lightAppearance')}
        </button>
        <button role="menuitem" className={styles.userMenuItem} onClick={() => { onStartTour(); setShowUserMenu(false); }}>
          <Icon name="play_circle" />{t('sidebar.takeTour')}
        </button>
        <div className={styles.userMenuSeparator} role="separator" />
        <button role="menuitem" className={`${styles.userMenuItem} ${styles.danger}`} onClick={logout}>
          <Icon name="logout" />{t('sidebar.logout')}
        </button>
      </div>
      </div>
    </div>
  );
};

export default Sidebar;